import { CountryCodes } from '@webapp/core/entities/external/Country'
import { formatCountryLabelFromCode } from '@webapp/core/utils/formatCountryLabel'
import { useFinancialAccountState, useProcurifyIntl } from '@webapp/hooks'
import { FinancialAccountTypes, PaymentMethodTypes } from '@webapp/models/enums'
import messages from './messages'
import { type PaymentMethod } from '../../entities/PaymentMethod'

export function useFormatPaymentMethodSummary({
  convertCountryCodeToName = true,
  showIdentifier = false,
}: {
  convertCountryCodeToName?: boolean
  showIdentifier?: boolean
} = {}) {
  const intl = useProcurifyIntl()

  const { financialAccountType } = useFinancialAccountState()

  const getFinancialAccountCountryCode = () => {
    switch (financialAccountType) {
      case FinancialAccountTypes.AIRWALLEX_CAD:
        return CountryCodes.CANADA
      case FinancialAccountTypes.AIRWALLEX_GBP:
        return CountryCodes.UNITED_KINGDOM_OF_GREAT_BRITAIN_AND_NORTHERN_IRELAND
      case FinancialAccountTypes.AIRWALLEX_USD:
      default:
        return CountryCodes.UNITED_STATES_OF_AMERICA
    }
  }

  return (paymentMethod: PaymentMethod): string | null => {
    const currencyName = paymentMethod.currency.name

    const financialAccountCountryCode = getFinancialAccountCountryCode()
    const countryCode = paymentMethod.isAirwallex()
      ? paymentMethod.bankAccountCountryCode
      : financialAccountCountryCode
    const countryName = convertCountryCodeToName
      ? formatCountryLabelFromCode(countryCode)
      : countryCode
    const countryLabel = countryName ? `${countryName} • ` : ``

    const buildSummaryText = () => {
      switch (paymentMethod.type) {
        case PaymentMethodTypes.AIRWALLEX:
          return `${countryLabel}${currencyName} • ${paymentMethod.paymentRail?.split('_').join(' ')}`
        case PaymentMethodTypes.PROCURIFY_ACH:
          return `${countryLabel}${currencyName} • ${intl.formatMessage(messages.achLabel)}`
        case PaymentMethodTypes.PROCURIFY_WIRE:
          return `${countryLabel}${currencyName} • ${intl.formatMessage(messages.wireLabel)}`
        case PaymentMethodTypes.PROCURIFY_CHECK:
          return `${countryLabel}${currencyName} • ${intl.formatMessage(messages.checkLabel)}`
        case PaymentMethodTypes.EFT:
          return `${countryLabel}${currencyName} • ${intl.formatMessage(messages.eftLabel)}`
        case PaymentMethodTypes.ACH:
          return `${intl.formatMessage(messages.externalLabel)} • ${currencyName} • ${intl.formatMessage(messages.achLabel)}`
        case PaymentMethodTypes.CHECK:
          return `${intl.formatMessage(messages.externalLabel)} • ${currencyName} • ${intl.formatMessage(messages.checkLabel)}`
        case PaymentMethodTypes.WIRE:
          return `${intl.formatMessage(messages.externalLabel)} • ${currencyName} • ${intl.formatMessage(messages.wireLabel)}`
        case PaymentMethodTypes.OTHER:
          return `${intl.formatMessage(messages.externalLabel)} • ${currencyName} • ${intl.formatMessage(messages.otherLabel)}`
        default:
          return null
      }
    }

    let summaryText = buildSummaryText()

    if (showIdentifier) {
      const identifier = (() => {
        switch (true) {
          case paymentMethod.isACH():
          case paymentMethod.isEFT():
          case paymentMethod.isProcurifyAch():
          case paymentMethod.isProcurifyWire():
            return paymentMethod.data.accountNumber
          case paymentMethod.isProcurifyCheck():
            return paymentMethod.data.payeeName
          case paymentMethod.isCheck():
            return paymentMethod.data.payableTo
          case paymentMethod.isAirwallex():
            return (
              paymentMethod.data.accountNumber ||
              paymentMethod.data.iban ||
              paymentMethod.data.swiftCode
            )
          default:
            return null
        }
      })()

      if (identifier) {
        summaryText += ` • ${identifier}`
      }
    }

    return summaryText
  }
}
